import { type ReactNode } from 'react';
import { MoonIcon, SunIcon } from 'lucide-react';
import { AuthMarketingHighlights } from '@/components/auth-marketing-highlights';
import { Button } from '@/components/ui/button';
import { useThemeCustomizer } from '@/lib/theme-customizer';
import { cn } from '@/lib/utils';

type AuthShellProps = {
  badge?: string;
  children: ReactNode;
  description: string;
  eyebrow?: string;
  title: string;
  quote?: ReactNode | null;
  quoteAuthor?: string;
  quoteRole?: string;
  variant?: 'default' | 'narrow-aside';
};

export function AuthShell({
  badge: badgeProp,
  children,
  description,
  eyebrow,
  title,
  quote = null,
  quoteAuthor = '',
  quoteRole = '',
  variant = 'default',
}: AuthShellProps) {
  const { effectiveMode, setMode } = useThemeCustomizer();
  const narrowAside = variant === 'narrow-aside';
  const badge = badgeProp?.trim();

  const hasCustomAsideContent = Boolean(
    eyebrow?.trim() || title?.trim() || description?.trim() || quote != null,
  );

  return (
    <main
      className={cn(
        'relative grid min-h-svh w-full lg:h-svh lg:max-h-svh lg:overflow-hidden bg-white text-foreground dark:bg-black dark:text-zinc-50',
        narrowAside ? 'lg:grid-cols-2 2xl:grid-cols-[3fr_7fr]' : 'lg:grid-cols-2',
      )}
    >
      {/* Theme Switcher Button */}
      <div className="absolute top-4 right-4 z-20">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            'size-10 rounded-full shadow-none transition-colors',
            effectiveMode === 'dark'
              ? 'bg-transparent text-white hover:bg-white/15 hover:text-white'
              : 'bg-transparent text-zinc-700 hover:bg-zinc-200/70 hover:text-zinc-900',
          )}
          onClick={() => setMode(effectiveMode === 'dark' ? 'light' : 'dark')}
          aria-label={effectiveMode === 'dark' ? 'Activer le mode clair' : 'Activer le mode sombre'}
          title={effectiveMode === 'dark' ? 'Activer le mode clair' : 'Activer le mode sombre'}
        >
          {effectiveMode === 'dark' ? <SunIcon className="size-5" /> : <MoonIcon className="size-5" />}
        </Button>
      </div>

      {/* Left Column: Modern Dark Card ISO Screenshot */}
      <div className="relative hidden min-h-0 p-5 sm:p-5 lg:p-6 xl:p-7 lg:flex lg:h-full lg:max-h-full">
        <aside
          className={cn(
            'auth-card-reveal-left relative flex h-full w-full min-h-0 flex-col justify-between overflow-hidden rounded-[16px] bg-black p-7 sm:p-8 lg:p-9 text-white shadow-2xl',
            narrowAside ? '2xl:p-6' : '',
          )}
        >
          {hasCustomAsideContent ? (
            <div
              className={cn(
                'auth-shell-reveal auth-shell-reveal--content my-auto w-full space-y-3',
                narrowAside && '2xl:max-w-[min(100%,18rem)]',
              )}
            >
              {eyebrow?.trim() ? (
                <p className="text-xs font-medium tracking-[0.2em] text-white/70 uppercase">
                  {eyebrow}
                </p>
              ) : null}
              {title?.trim() ? (
                <h1
                  className={cn(
                    'text-balance font-semibold leading-tight tracking-tight text-white',
                    narrowAside
                      ? 'text-2xl xl:text-3xl 2xl:text-xl'
                      : 'text-2xl xl:text-3xl',
                  )}
                >
                  {title}
                </h1>
              ) : null}
              {description?.trim() ? (
                <p
                  className={cn(
                    'text-pretty leading-relaxed text-zinc-200',
                    narrowAside ? 'text-sm xl:text-base' : 'text-sm xl:text-base',
                  )}
                >
                  {description}
                </p>
              ) : null}

              {quote != null ? (
                <blockquote
                  className={cn(
                    'auth-shell-reveal auth-shell-reveal--quote mt-5 w-full max-w-md border-none p-0 text-center',
                    narrowAside && '2xl:max-w-[min(100%,18rem)]',
                  )}
                >
                  <p
                    className={cn(
                      'text-balance font-medium leading-snug tracking-tight text-white',
                      narrowAside ? 'text-lg lg:text-xl' : 'text-lg lg:text-xl',
                    )}
                  >
                    « {quote} »
                  </p>
                  {quoteAuthor || quoteRole ? (
                    <footer className="mt-3 space-y-0.5 text-xs text-zinc-300">
                      {quoteAuthor ? (
                        <cite className="not-italic font-semibold text-white">{quoteAuthor}</cite>
                      ) : null}
                      {quoteRole ? <p>{quoteRole}</p> : null}
                    </footer>
                  ) : null}
                </blockquote>
              ) : null}
            </div>
          ) : (
            <AuthMarketingHighlights />
          )}
        </aside>
      </div>

      {/* Right Column: Form Section */}
      <section className="auth-card-reveal-right flex min-h-svh lg:min-h-0 lg:h-full flex-col justify-center overflow-y-auto px-6 py-8 sm:px-10 lg:px-12 xl:px-16">
        <div className="mx-auto w-full max-w-[24.5rem]">
          {badge ? (
            <p className="mb-8 text-xs font-medium tracking-[0.2em] text-muted-foreground uppercase">{badge}</p>
          ) : null}
          {children}
        </div>
      </section>
    </main>
  );
}
